import { ComponentType, ReactNode, SVGAttributes } from 'react';

import { BuilderName } from 'dashboard/models/inquiry-template';
import AgeImage from 'images/dashboard/onboarding/age.svg';
import DocumentImage from 'images/dashboard/onboarding/document.svg';
import GovIdImage from 'images/dashboard/onboarding/gov-id.svg';
import PepImage from 'images/dashboard/onboarding/pep.svg';
import PhoneImage from 'images/dashboard/onboarding/phone-2fa.svg';
import SelfieImage from 'images/dashboard/onboarding/selfie.svg';
import SsnImage from 'images/dashboard/onboarding/ssn.svg';
import WatchlistImage from 'images/dashboard/onboarding/watchlist.svg';
import { ApiModel, ApiResource } from 'lib/interfaces/api';

export interface BlueprintAttributes {
  name: string;
  status: BlueprintStatus;
  'source-type': string;
  'created-at': string;
  'updated-at': string;
  'archived-at': string;
  'supported-self-serve-plans': string[];
  blocks: BlueprintBlock[];
}

export enum BlueprintStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export interface BlueprintBlock {
  name: string;
  type: BlueprintBlockType;
  token: string;
  'latest-published-version-token': string;
}

export type Blueprint = ApiResource<BlueprintAttributes, {}, ApiModel.Blueprint>;

// Each Blueprint returned by /blueprint/catalog
export type BlueprintServerMetadata = {
  'is-usable-in-dashboard': boolean;
  'is-viewable-in-dashboard': boolean;
  description: string;
  'display-icon-keys': BlueprintIconKey[];
  key: string;
  'display-name': string;
  'required-self-serve-plan-keys-in-production-environment': string[];
};

export enum BlueprintIconKey {
  AgeImage = 'age_image',
  DocumentImage = 'document_image',
  GovIdImage = 'gov_id_image',
  PhoneImage = 'phone_image',
  PepImage = 'pep_image',
  SelfieImage = 'selfie_image',
  SsnImage = 'ssn_image',
  WatchlistImage = 'watchlist_image',
}

export const blueprintIconsMap: Record<
  BlueprintIconKey,
  ComponentType<SVGAttributes<SVGElement>>
> = {
  [BlueprintIconKey.AgeImage]: AgeImage,
  [BlueprintIconKey.DocumentImage]: DocumentImage,
  [BlueprintIconKey.GovIdImage]: GovIdImage,
  [BlueprintIconKey.PhoneImage]: PhoneImage,
  [BlueprintIconKey.PepImage]: PepImage,
  [BlueprintIconKey.SelfieImage]: SelfieImage,
  [BlueprintIconKey.SsnImage]: SsnImage,
  [BlueprintIconKey.WatchlistImage]: WatchlistImage,
};

// Used for Blueprints explicitly referenced on the client, such as when recommending specific Blueprints
export enum BlueprintKey {
  AgeVerification = 'age_verification',
  Kyc = 'kyc',
  KycAndAddress = 'kyc_and_address',
  KycCryptoAndWalletWatchlist = 'kyc_crypto_and_wallet_watchlist',
}

export enum BlueprintBlockType {
  InquiryTemplate = 'InquiryTemplate',
  VerificationTemplate = 'VerificationTemplate',
  ReportTemplate = 'ReportTemplate',
  Workflow = 'Workflow',
  CaseTemplate = 'CaseTemplate',
  TxnType = 'TxnType',
}

export type BlueprintPreviewMap = Partial<Record<string, BlueprintPreview>>;

export type BlueprintPreview = {
  blocks: BlueprintPreviewBlock[];
};

type BlueprintPreviewBlock = {
  name: string;
  'builder-name': BuilderName;
  'block-type': BlueprintBlockType;
};

export enum IndustryName {
  Crypto = 'crypto',
  ECommerce = 'e-commerce',
  Education = 'education',
  FinancialServices = 'financial-services',
  GigEconomy = 'gig-economy',
  Healthcare = 'healthcare',
  Insurance = 'insurance',
  PropertyTech = 'property-tech',
  Social = 'social',
  Other = 'other',
}

export const industryCatalog: Record<IndustryName, { label: string; icon: ReactNode }> = {
  [IndustryName.Crypto]: {
    label: 'Crypto',
    icon: <>&#9889;</>,
  },
  [IndustryName.ECommerce]: {
    label: 'E-Commerce',
    icon: <>&#128717;</>,
  },
  [IndustryName.Education]: {
    label: 'Education',
    icon: <>&#128218;</>,
  },
  [IndustryName.FinancialServices]: {
    label: 'Financial Services',
    icon: <>&#127974;</>,
  },
  [IndustryName.GigEconomy]: {
    label: 'Sharing and Gig Economy',
    icon: <>&#129782;</>,
  },
  [IndustryName.Healthcare]: {
    label: 'Healthcare',
    icon: <>&#128104;</>,
  },
  [IndustryName.Insurance]: {
    label: 'Insurance',
    icon: <>&#128737;</>,
  },
  [IndustryName.PropertyTech]: {
    label: 'Property Tech',
    icon: <>&#127968;</>,
  },
  [IndustryName.Social]: {
    label: 'Social',
    icon: <>&#128241;</>,
  },
  [IndustryName.Other]: {
    label: 'Other',
    icon: <>&#128640;</>,
  },
};

export enum UseCaseV2Name {
  AgeVerification = 'age-verification',
  CredentialVerification = 'credential-verification',
  DocumentVerification = 'document-verification',
  Kyb = 'kyb',
  KycAml = 'kyc-aml',
  OnboardBusinesses = 'onboard-businesses',
  OnboardCouriers = 'onboard-couriers',
  OnboardCustomers = 'onboard-customers',
  OnboardDrivers = 'onboard-drivers',
  OnboardGigWorkers = 'onboard-gig-workers',
  OnboardHosts = 'onboard-hosts',
  OnboardInstructors = 'onboard-instructors',
  OnboardPatients = 'onboard-patients',
  OnboardStudents = 'onboard-students',
  OnboardTenants = 'onboard-tenants',
  OnboardVendors = 'onboard-vendors',
  PreventCatfishing = 'prevent-catfishing',
  PurchaseMonitoring = 'purchase-monitoring',
  ReVerification = 're-verification',
  TourProperties = 'tour-properties',
  TransactionMonitoring = 'transaction-monitoring',
  NoneOfTheAbove = 'none-of-the-above',
}

export const useCasesV2Catalog: Record<UseCaseV2Name, { label: string; icon: ReactNode }> = {
  [UseCaseV2Name.AgeVerification]: {
    label: 'Verify age of users',
    icon: <>&#128118;</>,
  },
  [UseCaseV2Name.CredentialVerification]: {
    label: 'Verify credentials',
    icon: <>&#128195;</>,
  },
  [UseCaseV2Name.DocumentVerification]: {
    label: 'Verify documents',
    icon: <>&#128194;</>,
  },
  [UseCaseV2Name.Kyb]: {
    label: 'Conduct KYB',
    icon: <>&#127970;</>,
  },
  [UseCaseV2Name.KycAml]: {
    label: 'Conduct KYC/AML',
    icon: <>&#127963;</>,
  },
  [UseCaseV2Name.OnboardBusinesses]: {
    label: 'Onboard businesses',
    icon: <>&#127970;</>,
  },
  [UseCaseV2Name.OnboardCouriers]: {
    label: 'Onboard couriers',
    icon: <>&#128230;</>,
  },
  [UseCaseV2Name.OnboardCustomers]: {
    label: 'Onboard customers',
    icon: <>&#128587;</>,
  },
  [UseCaseV2Name.OnboardDrivers]: {
    label: 'Onboard drivers',
    icon: <>&#128663;</>,
  },
  [UseCaseV2Name.OnboardGigWorkers]: {
    label: 'Onboard gig workers',
    icon: <>&#128104;</>,
  },
  [UseCaseV2Name.OnboardHosts]: {
    label: 'Onboard hosts',
    icon: <>&#127968;</>,
  },
  [UseCaseV2Name.OnboardInstructors]: {
    label: 'Onboard instructors',
    icon: <>&#128105;</>,
  },
  [UseCaseV2Name.OnboardPatients]: {
    label: 'Onboard patients',
    icon: <>&#129658;</>,
  },
  [UseCaseV2Name.OnboardStudents]: {
    label: 'Onboard students',
    icon: <>&#128105;</>,
  },
  [UseCaseV2Name.OnboardTenants]: {
    label: 'Onboard tenants',
    icon: <>&#128106;</>,
  },
  [UseCaseV2Name.OnboardVendors]: {
    label: 'Onboard vendors',
    icon: <>&#128188;</>,
  },
  [UseCaseV2Name.PreventCatfishing]: {
    label: 'Prevent catfishing',
    icon: <>&#129465;</>,
  },
  [UseCaseV2Name.PurchaseMonitoring]: {
    label: 'Risky purchase monitoring',
    icon: <>&#129534;</>,
  },
  [UseCaseV2Name.ReVerification]: {
    label: 'Re-verify users',
    icon: <>&#128257;</>,
  },
  [UseCaseV2Name.TourProperties]: {
    label: 'Tour properties',
    icon: <>&#127968;</>,
  },
  [UseCaseV2Name.TransactionMonitoring]: {
    label: 'Monitor high-risk transactions',
    icon: <>&#128184;</>,
  },
  [UseCaseV2Name.NoneOfTheAbove]: {
    label: 'None of the above',
    icon: <>&#129300;</>,
  },
};

export const industryToUseCasesMap: Record<IndustryName, UseCaseV2Name[]> = {
  [IndustryName.Crypto]: [
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.Kyb,
    UseCaseV2Name.KycAml,
    UseCaseV2Name.ReVerification,
    UseCaseV2Name.TransactionMonitoring,
  ],
  [IndustryName.ECommerce]: [
    UseCaseV2Name.AgeVerification,
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.OnboardBusinesses,
    UseCaseV2Name.OnboardCustomers,
    UseCaseV2Name.ReVerification,
  ],
  [IndustryName.Education]: [
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.OnboardInstructors,
    UseCaseV2Name.OnboardStudents,
    UseCaseV2Name.ReVerification,
  ],
  [IndustryName.FinancialServices]: [
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.Kyb,
    UseCaseV2Name.KycAml,
    UseCaseV2Name.ReVerification,
    UseCaseV2Name.TransactionMonitoring,
  ],
  [IndustryName.GigEconomy]: [
    UseCaseV2Name.AgeVerification,
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.OnboardBusinesses,
    UseCaseV2Name.OnboardCustomers,
    UseCaseV2Name.OnboardGigWorkers,
    UseCaseV2Name.OnboardHosts,
    UseCaseV2Name.ReVerification,
  ],
  [IndustryName.Healthcare]: [
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.OnboardBusinesses,
    UseCaseV2Name.OnboardPatients,
    UseCaseV2Name.ReVerification,
  ],
  [IndustryName.Insurance]: [
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.Kyb,
    UseCaseV2Name.KycAml,
    UseCaseV2Name.ReVerification,
    UseCaseV2Name.TransactionMonitoring,
  ],
  [IndustryName.PropertyTech]: [
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.OnboardHosts,
    UseCaseV2Name.OnboardTenants,
    UseCaseV2Name.ReVerification,
  ],
  [IndustryName.Social]: [
    UseCaseV2Name.AgeVerification,
    UseCaseV2Name.OnboardBusinesses,
    UseCaseV2Name.PreventCatfishing,
    UseCaseV2Name.ReVerification,
  ],
  [IndustryName.Other]: [
    UseCaseV2Name.AgeVerification,
    UseCaseV2Name.DocumentVerification,
    UseCaseV2Name.OnboardBusinesses,
    UseCaseV2Name.OnboardCustomers,
    UseCaseV2Name.ReVerification,
  ],
};
